// Library methods
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import EditEMRConnectionModal from "../EditEMRConnectionModal/EditEMRConnectionModal";
import ReportOutbox from "../ReportOutbox/ReportOutbox";
import WorklistToday from "../WorklistToday/WorklistToday";
import LastImport from "../LastImport/LastImport";
import SystemCard from "../SystemCard/SystemCard";
import JobRow, { getJobStatus } from "../JobRow/JobRow";

// Types
import {
  EMRCatalogDeviceType,
  EMRConnectionType,
} from "src/services/emr/types";

// Hooks
import { useDeleteEMRHubConnection } from "src/services/emr/hooks/emr-connections/useDeleteEMRHubConnection";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import {
  describeConnectionJob,
  describeDicomAddress,
  getJobDirection,
  getJobTitle,
  importsExams,
  isReportDestination,
} from "src/utils/emr/jobDescriptionHelper";
import { getDeviceDisplayName } from "src/utils/emr/pacServerHelper";

type ConnectionCardType = {
  connection: EMRConnectionType;
  hubIsActive: boolean;
  device?: EMRCatalogDeviceType;
  icon: ReactNode;
  sharedFolderPaths: Array<string> | undefined;
  renderEditModal?: (onClose: () => void) => ReactNode;
};

const ConnectionCard = ({
  connection,
  hubIsActive,
  device,
  icon,
  sharedFolderPaths,
  renderEditModal,
}: ConnectionCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const [openEditConnectionModal, setOpenEditConnectionModal] = useState(false);
  const [openDeleteConnectionModal, setOpenDeleteConnectionModal] =
    useState(false);

  const { mutate: deleteConnection, isPending: isDeletingConnection } =
    useDeleteEMRHubConnection(clinicInfo?.id, connection?.hubId);

  const subtitle =
    getDeviceDisplayName(device) ||
    connection.config?.path ||
    describeDicomAddress(connection.config);
  const canEditInForm = connection.sourceKind !== "ExportFolder";
  const closeEditModal = () => setOpenEditConnectionModal(false);

  return (
    <>
      <SystemCard
        testId={`connection-card-${connection.connectionId}`}
        icon={icon}
        name={connection.name}
        subtitle={subtitle}
        onEdit={
          renderEditModal || canEditInForm
            ? () => setOpenEditConnectionModal(true)
            : undefined
        }
        onDelete={() => setOpenDeleteConnectionModal(true)}
      >
        <JobRow
          direction={getJobDirection(connection)}
          title={t(getJobTitle(connection))}
          description={describeConnectionJob(connection, t)}
          status={getJobStatus(connection, hubIsActive)}
        />
        {isReportDestination(connection) && (
          <ReportOutbox
            clinicId={clinicInfo?.id}
            connectionId={connection.connectionId}
          />
        )}
        {connection.sourceKind === "Worklist" && (
          <WorklistToday
            clinicId={clinicInfo?.id}
            connectionId={connection.connectionId}
          />
        )}
        {importsExams(connection) && (
          <LastImport lastImport={connection.lastImport} />
        )}
      </SystemCard>

      {renderEditModal
        ? openEditConnectionModal && renderEditModal(closeEditModal)
        : canEditInForm && (
            <EditEMRConnectionModal
              open={openEditConnectionModal}
              clinicId={clinicInfo?.id}
              connection={connection}
              device={device}
              sharedFolderPaths={sharedFolderPaths}
              onClose={closeEditModal}
            />
          )}
      <SharedModal
        title={t("Delete Connection?")}
        modalDescription={t(
          "Are you sure you want to delete the selected connection?"
        )}
        confirmButtonText={t("Delete")}
        confirmButtonVariant="error"
        isDisabledButton={isDeletingConnection}
        open={openDeleteConnectionModal}
        onClose={() => setOpenDeleteConnectionModal(false)}
        onConfirm={() => {
          deleteConnection(connection?.connectionId, {
            onSuccess: () => setOpenDeleteConnectionModal(false),
          });
        }}
      />
    </>
  );
};

export default ConnectionCard;
